import React from 'react';
import { Field } from "react-final-form";
import {
    Box,
    Typography,
    Tooltip,
    Grid,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import {
    genusSpeciesAutoCompleteOptions,
    gonotrophicAutoCompleteOptions,
    lifeStatusAutoCompleteOptions,
} from "../../../../../utils/const";
import {
    renderAutocomplete,
} from "../../../../Forms/FormFields";
import AutofillSettingModal from "./AutoFillSettingModal";
import ResponsiveButton from "../../../../Forms/ResponsiveButton";
import ResponsiveIconButton from "../../../../Forms/ResponsiveIconButton";
import { RenderAutocompleteMultiple } from "../../../../Forms/renderAutocompleteMultiple";
import SexInput from "./Components/SexInput";
import {
    checkCommaAndBackSlash,
    checkCommaAndBackSlashOnTags,
    checkWellNumber,
} from "../../../../../lib/validationHelper";
import HiddenField from "./Components/HiddenField";
import _ from "lodash";

const buttonStyles = {
    autofillButton: {
        color: "status.info",
        borderColor: "status.info",
    },
    infoButton: {
        color: "status.info",
    },
};


const margin5rem = [
    "0.5rem !important",
    "0.5rem !important",
    "0.5rem !important",
    "0.5rem !important",
    "0.5rem !important",
    "0.5rem !important",
];
const smallmarginrem = [
    "0.05rem !important",
    "0.05rem !important",
    "0.05rem !important",
    "0.05rem !important",
    "0.05rem !important",
    "0.05rem !important",
];

const SpecimenFormLayout = ({
    shouldAllowAutofill,
    setAutofillTooltip,
    clearToolTip,
    autofillModalOpen,
    setAutofillModalOpen,
    form,
    specimenNum,
    lastSpecimenNum,
    specimenValues,
    autofillSettings,
    autofillTooltip,
    handleAutofill,
    highLightSpecies,
    hideFormErrors,
    normalizeGenusSpecies,
    vectorType,
    onBlurGenusSpecies,
    highLightSex,
    change,
    tagOptions,
    highLightLifeStage,
    highLightGonotrophic,
    highLightPlateAndWell,
    plateAutoCompleteOptions,
    onBlurPlate,
    onBlurWell,
    normalizeWell,
    wellAutocompleteOptions
}) => {

    return (<>
        <Box
            className={"flex border-b-2 border-black-100 justify-between"}
            style={{ paddingBottom: "8px" }}
            sx={{
                paddingBottom: ["8px", "8px", "8px", "8px", "8px", "8px"],
                mx: ["1rem", "1rem", "1rem", "1rem", "2rem", "2rem"],
            }}
        >
            <Typography
                component="span"
                style={{ alignSelf: "center" }}
                sx={{
                    fontWeight: 500,
                    fontSize: [
                        ".75rem",
                        ".75rem",
                        ".75rem",
                        ".75rem",
                        "1.15rem",
                        "1.25rem",
                    ],
                }}
            >
                Contributed Data
            </Typography>
            <div>
                <Tooltip
                    title={
                        <div>
                            <Typography variant="subtitle1">
                                {!shouldAllowAutofill?.autofillSettingsFilled &&
                                    "Update autofill settings before autofilling\n"}
                            </Typography>
                            <Typography variant="subtitle1">
                                {!shouldAllowAutofill?.metadataFilled &&
                                    shouldAllowAutofill?.autofillSettingsFilled &&
                                    "Enter data for current specimen before autofilling or filling with empty data\n"}
                            </Typography>
                            <Typography variant="subtitle1">
                                {shouldAllowAutofill.metadataFilled &&
                                    shouldAllowAutofill.autofillSettingsFilled &&
                                    "Enter data for at least one of the selected fields before autofilling or filling with empty data\n"}
                            </Typography>
                        </div>
                    }
                    onOpen={() => setAutofillTooltip(true)}
                    open={
                        !shouldAllowAutofill?.shouldAllowAutofill && autofillTooltip
                    }
                    onClose={() => clearToolTip()}
                >
                    <span>
                        <ResponsiveButton
                            className={"autofill-setting"}
                            // className={classes.autofillButton}
                            sx={buttonStyles.autofillButton}
                            // disabled={!shouldAllowAutofill?.shouldAllowAutofill}
                            variant={"outlined"}
                            onClick={() => handleAutofill(form)}
                        >
                            Autofill{" "}
                            {specimenNum
                                ? `${specimenNum + 1}-${lastSpecimenNum + 1}`
                                : `1-12`}
                        </ResponsiveButton>
                    </span>
                </Tooltip>
                <Tooltip
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={<Typography>&darr;</Typography>}
                    placement="top"
                    open={
                        !shouldAllowAutofill?.shouldAllowAutofill &&
                        autofillTooltip &&
                        !shouldAllowAutofill?.autofillSettingsFilled
                    }
                >
                    <ResponsiveIconButton>
                        <SettingsIcon
                            // className={classes.infoButton}
                            sx={buttonStyles.infoButton}
                            onClick={() => setAutofillModalOpen(true)}
                        />
                    </ResponsiveIconButton>
                </Tooltip>
                <AutofillSettingModal
                    autofillModalOpen={autofillModalOpen}
                    setAutofillModalOpen={setAutofillModalOpen}
                />
            </div>
        </Box>
        <form className={"flex flex-col"} id={"specimen-details-form"}>
            <Grid
                container
                sx={{
                    px: ["1rem", "1rem", "1rem", "1rem", "2rem", "2rem"],
                    pt: ["0.5rem", "0.5rem", "0.5rem", "0.5rem", "1rem", "1rem"],
                }}
                spacing={2}
            >
                <Grid
                    item
                    xs={6}
                    sx={{
                        display: autofillSettings?.speciesDisplay ? "flex" : "none",
                        backgroundColor: highLightSpecies
                            ? "rgba(99, 179, 237, 0.25)"
                            : "",
                    }}
                    data-testid="SpeciesBox"
                >
                    <Field
                        label="Species"
                        name={`genusSpecies`}
                        inputProps={{
                            id: `genus-species`,
                        }}
                        hideFormErrors={hideFormErrors}
                        normalize={normalizeGenusSpecies}
                        component={renderAutocomplete}
                        options={
                            (vectorType || "").startsWith("ti")
                                ? genusSpeciesAutoCompleteOptions?.["tick"]
                                : (vectorType || "").startsWith("mo")
                                    ? genusSpeciesAutoCompleteOptions?.["mosquito"]
                                    : [
                                        ...genusSpeciesAutoCompleteOptions?.["mosquito"],
                                        ...genusSpeciesAutoCompleteOptions?.["tick"],
                                    ]
                        }
                        autocompleteprops={{
                            getOptionLabel: (option) => option,
                            isOptionEqualToValue: (option, value) =>
                                option.toLowerCase() === value?.toLowerCase(),
                        }}
                        // onChange={(event, value) => setGenusSpecies(value)}
                        onBlur={(event, value) => onBlurGenusSpecies(value, form)}
                        helperText="Taxonomist determined ID"
                        multilineFlag={true}
                        validate={checkCommaAndBackSlash}
                    />
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{
                        display: autofillSettings?.sexDisplay ? "flex" : "none",
                        backgroundColor: highLightSex
                            ? "rgba(99, 179, 237, 0.25)"
                            : "",
                    }}
                    data-testid="SexBox"
                >
                    <SexInput
                        hideFormErrors={hideFormErrors}
                        form={form}
                        change={change}
                    />
                </Grid>
                <Grid
                    item
                    sx={{ mt: smallmarginrem }}
                    data-testid="SpecimenTagList"
                    xs={12}
                >
                    <Field
                        multi={"true"}
                        label="Specimen Tags"
                        name={`tags`}
                        inputProps={{ id: `tags` }}
                        hideFormErrors={hideFormErrors}
                        component={RenderAutocompleteMultiple}
                        fieldValue={form?.values?.tags || []}
                        options={tagOptions || []}
                        onChange={(event, value) => {
                            form.form.mutators.setValue("tags", [...value]);
                        }}
                        onBlur={(e) => e.preventDefault()}
                        multiple={true}
                        validate={checkCommaAndBackSlashOnTags}
                    />
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{
                        mt: margin5rem,
                        mb: margin5rem,
                        display: autofillSettings?.lifeStageDisplay ? "flex" : "none",
                        backgroundColor: highLightLifeStage
                            ? "rgba(99, 179, 237, 0.25)"
                            : "",
                    }}
                >
                    <Field
                        label="Life Stage"
                        name={`lifeStage`}
                        inputProps={{ id: `life-stage-field` }}
                        component={renderAutocomplete}
                        options={lifeStatusAutoCompleteOptions}
                        helperText="Life Stage"
                        validate={checkCommaAndBackSlash}
                    />
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{
                        mt: margin5rem,
                        mb: margin5rem,
                        display: autofillSettings?.gonotrophicDisplay
                            ? "flex"
                            : "none",
                        backgroundColor: highLightGonotrophic
                            ? "rgba(99, 179, 237, 0.25)"
                            : "",
                    }}
                >
                    <Field
                        label="Gonotrophic"
                        name={`gonotrophic`}
                        inputProps={{ id: `gonotrophic-field` }}
                        component={renderAutocomplete}
                        options={gonotrophicAutoCompleteOptions}
                        helperText="Feeding Status"
                        validate={checkCommaAndBackSlash}
                    />
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{
                        mt: margin5rem,
                        mb: margin5rem,
                        display: autofillSettings?.molecularDisplay ? "flex" : "none",
                        backgroundColor: highLightPlateAndWell
                            ? "rgba(99, 179, 237, 0.25)"
                            : "",
                    }}
                >
                    <Field
                        label="Unique Plate Number"
                        name={`plate`}
                        inputProps={{ id: `plate-field` }}
                        hideFormErrors={hideFormErrors}
                        component={renderAutocomplete}
                        options={plateAutoCompleteOptions}
                        onBlur={(event) => onBlurPlate(event.target.value, form)}
                        helperText="Molecular ID Plate"
                        validate={checkCommaAndBackSlash}
                    />
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{
                        mt: margin5rem,
                        mb: margin5rem,
                        display: autofillSettings?.molecularDisplay ? "flex" : "none",
                        backgroundColor: highLightPlateAndWell
                            ? "rgba(99, 179, 237, 0.25)"
                            : "",
                    }}
                >
                    <Field
                        label="Well Number"
                        name={`well`}
                        inputProps={{ id: `well-field` }}
                        normalize={normalizeWell}
                        hideFormErrors={hideFormErrors}
                        component={renderAutocomplete}
                        options={wellAutocompleteOptions}
                        onBlur={(event, value) => {
                            onBlurWell(event.target.value, form);
                        }}
                        helperText="Molecular ID Well( A1 - H12)"
                        validate={checkWellNumber}
                    />
                </Grid>
                <Grid item xs={12} sx={{ mt: margin5rem }}>
                    <HiddenField
                        text={_.capitalize(
                            specimenValues?.genusSpecies?.toLowerCase()
                        )}
                        Name="Species: "
                        display={
                            specimenValues?.genusSpecies?.length > 0 &&
                            !autofillSettings?.speciesDisplay
                        }
                        isItalic={true}
                    />
                    <HiddenField
                        text={specimenValues?.sex}
                        Name="Sex: "
                        display={
                            specimenValues?.sex?.length > 0 &&
                            !autofillSettings?.sexDisplay
                        }
                    />
                    <HiddenField
                        text={specimenValues?.lifeStage}
                        Name="Life Stage: "
                        display={
                            specimenValues?.lifeStage?.length > 0 &&
                            !autofillSettings?.lifeStageDisplay
                        }
                    />
                    <HiddenField
                        text={specimenValues?.gonotrophic}
                        Name="Gonotrophic: "
                        display={
                            specimenValues?.gonotrophic?.length > 0 &&
                            !autofillSettings?.gonotrophicDisplay
                        }
                    />
                    <HiddenField
                        text={specimenValues?.plate}
                        Name="Plate: "
                        display={
                            !autofillSettings?.molecularDisplay &&
                            specimenValues?.plate &&
                            specimenValues?.well
                        }
                    />
                    <HiddenField
                        text={specimenValues?.well}
                        Name="Well: "
                        display={
                            !autofillSettings?.molecularDisplay &&
                            specimenValues?.plate &&
                            specimenValues?.well
                        }
                    />
                </Grid>
            </Grid>
        </form>
    </>)


}
export default SpecimenFormLayout;